// Broadcast data, callables and models (PRD §4.6 / WP-02).
// Full WhatChimp Parity v2 implementation with zero mock dependencies.

import { useEffect, useState } from 'react';
import { collection, limit, onSnapshot, orderBy, query } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from './firebase';
import { stripUndefined } from './callable';
import { useTenant } from './tenant';

function call<TReq, TRes>(name: string) {
  const fn = httpsCallable<TReq, TRes>(functions, name);
  return (data: TReq): Promise<TRes> => fn(stripUndefined(data)).then((r) => r.data);
}

export type BcastStatus =
  | 'draft'
  | 'scheduled'
  | 'sending'
  | 'paused'
  | 'completed'
  | 'cancelled'
  | 'failed';

export type BcastType = '24h' | 'anytime';

export interface BcastMetric {
  pct: number;
  sub: string;
}

export interface BroadcastCounters {
  queued: number;
  sent: number;
  delivered: number;
  read: number;
  failed: number;
  unreached: number;
  processed: number;
  opened: number;
}

export interface BroadcastSegment {
  search?: string;
  status?: 'pending' | 'subscribed' | 'unsubscribed' | Array<'pending' | 'subscribed' | 'unsubscribed'>;
  includeLabelIds?: string[];
  excludeLabelIds?: string[];
  includeLabels?: string[];
  excludeLabels?: string[];
  includeListIds?: string[];
  excludeListIds?: string[];
  country?: string | string[];
  language?: string | string[];
  customField?: {
    key: string;
    op: 'eq' | 'neq' | 'contains' | 'exists' | 'not_exists' | 'gt' | 'gte' | 'lt' | 'lte';
    value?: any;
  };
  customFields?: Record<string, any>;
  marketingOptInRequired?: boolean;
}

export interface HeaderMedia {
  storagePath?: string;
  url?: string;
  mime?: string;
  mediaType?: 'image' | 'video' | 'document';
  mediaId?: string;
}

export interface BroadcastDoc {
  id: string;
  name: string;
  channel?: 'whatsapp' | 'sms';
  smsProfileId?: string | null;
  smsBody?: string | null;
  type: BcastType;
  status: BcastStatus;
  templateId?: string | null;
  templateName?: string | null;
  templateLanguage?: string;
  freeformBody?: string | null;
  variableOverrides?: Record<string, string> | null;
  headerMedia?: HeaderMedia | null;
  segment?: BroadcastSegment;
  assignLabelAfter?: string | null;
  delaySeconds?: number;
  ratePerSec?: number;
  scheduledFor?: number | null;
  timezone?: string;
  estimatedReach?: number;
  costEstimate?: number | { totalCost: number; currency: string };
  counters?: BroadcastCounters;
  integrationId?: string;
  createdBy?: string;
  createdAt: number;
  updatedAt: number;
  pausedFrom?: string | null;
  pausedAt?: number | null;
  resumedAt?: number | null;
  cancelledAt?: number | null;
  completedAt?: number | null;
}

export interface RecipientRow {
  id: string;
  tenantId?: string;
  broadcastId?: string;
  subscriberId: string;
  phoneE164: string;
  name: string | null;
  status: 'queued' | 'sent' | 'sandbox' | 'delivered' | 'read' | 'failed' | 'unreached';
  providerRef?: string | null;
  error?: string | null;
  errorCode?: string | number | null;
  sentAt?: number;
  deliveredAt?: number;
  readAt?: number;
  failedAt?: number;
  createdAt?: number;
  updatedAt?: number;
}

export interface BroadcastStats {
  totalCampaigns: number;
  completed: number;
  avgOpenRate: number;
  totalSent: number;
  failedUnreachedRate: number;
}

export interface Campaign {
  id: string | number;
  name: string;
  channel?: 'whatsapp' | 'sms';
  smsProfileId?: string | null;
  smsBody?: string | null;
  type: BcastType;
  status: BcastStatus;
  processed: BcastMetric;
  delivered: BcastMetric;
  opened: BcastMetric;
  unreached: BcastMetric;
  recipients: number;
  scheduledAt: string;
  integrationId?: string;
  templateName?: string | null;
  createdAt?: number;
  raw?: BroadcastDoc;
}

export interface BroadcastsState {
  broadcasts: Campaign[];
  loading: boolean;
}

// ── Helpers ──────────────────────────────────────────────────────────────────

export function statusToAllowedActions(status: BcastStatus) {
  return {
    canView: true,
    canPause: status === 'sending' || status === 'scheduled',
    canResume: status === 'paused',
    canCancel: status === 'sending' || status === 'scheduled' || status === 'paused',
    canExport: true,
    canDelete: status === 'draft' || status === 'completed' || status === 'cancelled' || status === 'failed',
  };
}

export function formatScheduledTime(ts: number | null | undefined, timezone?: string): string {
  if (!ts) return '—';
  try {
    const opts: Intl.DateTimeFormatOptions = {
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    };
    if (timezone) {
      opts.timeZone = timezone;
    }
    return new Date(ts).toLocaleString([], opts);
  } catch {
    return new Date(ts).toLocaleString([], {
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
    });
  }
}

export function mapBroadcast(d: any): Campaign {
  const c = d.counters ?? {};
  const reach = d.estimatedReach ?? c.queued ?? 0;
  const pct = (n: number) => (reach > 0 ? Math.min(100, Math.round((n / reach) * 100)) : 0);
  const metric = (n: number) => ({ pct: pct(n), sub: reach ? `${n}/${reach}` : '—' });

  const sent = Number(c.sent ?? 0);
  const failed = Number(c.failed ?? 0);
  const unreached = Number(c.unreached ?? 0);
  const processedCount = Number(c.processed ?? (sent + failed + unreached));
  const deliveredCount = Number(c.delivered ?? 0);
  const openedCount = Number(c.read ?? c.opened ?? 0);

  return {
    id: d.id,
    name: d.name ?? (d.templateName ? `${d.templateName} · ${new Date(d.createdAt || Date.now()).toLocaleDateString()}` : 'Campaign'),
    channel: d.channel || 'whatsapp',
    smsProfileId: d.smsProfileId || null,
    smsBody: d.smsBody || null,
    type: d.type === '24h' ? '24h' : 'anytime',
    status: d.status ?? 'draft',
    processed: metric(processedCount),
    delivered: metric(deliveredCount),
    opened: metric(openedCount),
    unreached: metric(unreached),
    recipients: reach,
    scheduledAt: formatScheduledTime(d.scheduledFor, d.timezone),
    integrationId: d.integrationId,
    templateName: d.templateName,
    createdAt: d.createdAt,
    raw: d,
  };
}

// ── Hook: Real-time listener for tenant campaigns ────────────────────────────

export function useBroadcasts(): BroadcastsState {
  const { tenantId } = useTenant();
  const [broadcasts, setBroadcasts] = useState<Campaign[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!tenantId) {
      setBroadcasts([]);
      setLoading(false);
      return;
    }
    const q = query(
      collection(db, `tenants/${tenantId}/broadcasts`),
      orderBy('createdAt', 'desc'),
      limit(100),
    );
    return onSnapshot(
      q,
      (snap) => {
        setBroadcasts(snap.docs.map((d) => mapBroadcast({ id: d.id, ...d.data() })));
        setLoading(false);
      },
      () => setLoading(false),
    );
  }, [tenantId]);

  return { broadcasts, loading };
}

// ── Callables (WP-02 Backend) ────────────────────────────────────────────────

export const whatsappCreateBroadcast = call<
  {
    name: string;
    channel?: 'whatsapp' | 'sms';
    smsProfileId?: string;
    smsBody?: string;
    integrationId?: string;
    templateId?: string;
    templateName?: string;
    templateLanguage?: string;
    type?: '24h' | 'anytime';
    variableOverrides?: Record<string, string>;
    headerMedia?: HeaderMedia;
    segment?: BroadcastSegment;
    assignLabelAfter?: string;
    delaySeconds?: number;
    scheduledFor?: string | number;
    timezone?: string;
    dryRun?: boolean;
  },
  { id: string; status: string; reach: number; costEstimate: number | { totalCost: number; currency: string } }
>('whatsappCreateBroadcast');

export const whatsappEstimateReach = call<
  {
    segment?: BroadcastSegment;
    templateId?: string;
    category?: string;
    type?: '24h' | 'anytime';
  },
  { ok: boolean; count: number; costEstimate: number }
>('whatsappEstimateReach');

export const whatsappSubmitBroadcast = call<
  { broadcastId: string },
  { status: 'scheduled' | 'sending'; reach: number }
>('whatsappSubmitBroadcast');

export const whatsappPauseBroadcast = call<
  { broadcastId: string; reason?: string },
  { ok: boolean; status: 'paused' }
>('whatsappPauseBroadcast');

export const whatsappResumeBroadcast = call<
  { broadcastId: string; reason?: string },
  { ok: boolean; status: 'scheduled' | 'sending' }
>('whatsappResumeBroadcast');

export const whatsappCancelBroadcast = call<
  { broadcastId: string; reason?: string },
  { ok: boolean; status: 'cancelled' }
>('whatsappCancelBroadcast');

export const whatsappDeleteBroadcast = call<
  { broadcastId: string },
  { ok: boolean }
>('whatsappDeleteBroadcast');

export const whatsappBroadcastDetail = call<
  { id: string; cursor?: string; limit?: number },
  {
    ok: boolean;
    broadcast: BroadcastDoc;
    counters: BroadcastCounters;
    recipients: RecipientRow[];
    nextCursor: string | null;
  }
>('whatsappBroadcastDetail');

export const whatsappBroadcastRecipients = call<
  { id: string; status?: string; cursor?: string; limit?: number },
  {
    ok: boolean;
    recipients: RecipientRow[];
    nextCursor: string | null;
  }
>('whatsappBroadcastRecipients');

export const whatsappExportBroadcastReport = call<
  { id: string },
  { ok: boolean; downloadUrl: string; totalRows: number }
>('whatsappExportBroadcastReport');

export const whatsappBroadcastStats = call<
  { days?: number; channel?: 'whatsapp' | 'sms' },
  {
    ok: boolean;
    totalCampaigns: number;
    completed: number;
    avgOpenRate: number;
    totalSent: number;
    failedUnreachedRate: number;
  }
>('whatsappBroadcastStats');

export const whatsappListBroadcasts = call<
  {
    integrationId?: string;
    status?: string;
    channel?: 'whatsapp' | 'sms';
    search?: string;
    cursor?: string;
    limit?: number;
  },
  {
    ok: boolean;
    broadcasts: BroadcastDoc[];
    nextCursor: string | null;
  }
>('whatsappListBroadcasts');

// Compatibility aliases
export const createBroadcast = whatsappCreateBroadcast;
export const estimateReach = whatsappEstimateReach;
export const submitBroadcast = whatsappSubmitBroadcast;
export const cancelBroadcast = whatsappCancelBroadcast;
